import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { markCompleted } from '../../services/tutorialService';
import './DuelCoach.css';

// Coach du duel tutoriel : suite d'objectifs deduits de l'etat de la partie.
// Aucune modification du moteur : le joueur joue un vrai duel (IA facile)
// guide etape par etape.
function computeObjectives(gs) {
  if (!gs) return { index: 0, total: 5 };
  const playerMonsters = (gs.playerField || []).filter((c) => c.zone === 'monster' || c.zone === 'battle' || c.zone === 'field');
  const hasSummoned = playerMonsters.length > 0;
  const hasDeclared = !!(gs.pendingAttack && gs.pendingAttack.attackers
    && gs.pendingAttack.attackers.some((a) => a.player !== 'opponent'));
  const turnAtLeastTwo = (gs.turn || 1) >= 2;

  let idx = 0;
  if (hasSummoned) idx = 1;
  if (hasSummoned && turnAtLeastTwo && hasDeclared) idx = 2;
  // L'attaque est resolue : plus de pendingAttack alors qu'on avait declenche
  const resolved = hasSummoned && turnAtLeastTwo
    && (!gs.pendingAttack || !gs.pendingAttack.attackers || gs.pendingAttack.attackers.length === 0)
    && idx >= 1;
  if (resolved) idx = Math.max(idx, 3);
  return { index: Math.min(idx, 4), total: 5 };
}

const OBJECTIVES = [
  {
    icon: '🖐️',
    title: 'Invoque ta première créature',
    body: "Glisse une carte de ta main vers la zone des monstres, à l'endroit exact où tu veux la poser (gauche, droite ou entre deux cartes).",
  },
  {
    icon: '⚔️',
    title: 'Déclare une attaque',
    body: "À partir du 2e tour : glisse un monstre vers la zone de combat pour le déclarer attaquant. Là aussi, choisis sa position au doigt.",
  },
  {
    icon: '💥',
    title: 'Résous le combat',
    body: "Confirme la résolution (bouton d'action) : les attaquants fondent sur la cible. Sans bloqueur adverse, ils frappent directement les PV !",
  },
  {
    icon: '🔄',
    title: 'Finis ton tour',
    body: "Termine ton tour et observe l'IA. Si elle attaque, glisse un de tes monstres SUR son attaquant pour le bloquer.",
  },
  {
    icon: '🏆',
    title: 'Joue librement',
    body: "Continue jusqu'à la victoire ! Utilise sorts, pièges et terrains. Bon duel, Duelliste !",
  },
];

export default function DuelCoach({ gameState }) {
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem('elementum_tutorial_coach_closed') === '1'; } catch (e) { return false; }
  });
  const [marked, setMarked] = useState(false);

  const isGameOver = !!(gameState && gameState.isGameOver);
  const { index, total } = useMemo(() => computeObjectives(gameState), [gameState]);

  useEffect(() => {
    if (isGameOver && !marked) {
      setMarked(true);
      markCompleted();
    }
  }, [isGameOver, marked]);

  if (dismissed && !isGameOver) return null;

  const close = () => {
    try { sessionStorage.setItem('elementum_tutorial_coach_closed', '1'); } catch (e) { /* noop */ }
    markCompleted();
    setDismissed(true);
  };

  return (
    <div className="duel-coach">
      <button type="button" className="duel-coach__close" onClick={close} aria-label="Fermer le tutoriel">✕</button>
      {isGameOver ? (
        <div className="duel-coach__inner">
          <span className="duel-coach__icon">🎓</span>
          <div>
            <div className="duel-coach__title">Tutoriel terminé !</div>
            <div className="duel-coach__body">
              Tu maîtrises les bases. Rendez-vous sur la page « Jouer » pour ton premier vrai duel.
            </div>
            <button
              type="button"
              className="duel-coach__btn"
              onClick={() => navigate('/game/matchmaking')}
            >
              Retour aux parties →
            </button>
          </div>
        </div>
      ) : (
        <div className="duel-coach__inner">
          <span className="duel-coach__icon">{OBJECTIVES[index].icon}</span>
          <div>
            <div className="duel-coach__step">Objectif {index + 1}/{total}</div>
            <div className="duel-coach__title">{OBJECTIVES[index].title}</div>
            <div className="duel-coach__body">{OBJECTIVES[index].body}</div>
          </div>
        </div>
      )}
    </div>
  );
}
